/* eslint-disable max-len */
import _ from 'underscore';
import classnames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { List, FocusZone } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';

import { TilesGridLoadingCell } from './grid-cells/tiles-grid-loading-cell';
import { getTilesGridStyle } from './styles/tiles-grid-style';
import { DEFAULT_CONFIG } from './default-grid-config';

// export to do UT
export function calcColumnWidth(surfaceWidth, maxColumnWidth, minColumnWidth) {
  const colCntOfMaxWidth = Math.ceil(surfaceWidth / maxColumnWidth);
  const colCntOfMinWidth = Math.floor(surfaceWidth / minColumnWidth);
  let columnCount = Math.min(colCntOfMaxWidth, colCntOfMinWidth) || 1;
  let columnWidth = Math.floor(surfaceWidth / columnCount);
  columnWidth = Math.min(Math.max(minColumnWidth, columnWidth), maxColumnWidth);
  columnCount = Math.floor(surfaceWidth / columnWidth) || 1;
  return [columnWidth, columnCount];
}

const LOADING_CELL = {};
const FIRST_LOADING_CELL = {};

const TilesGridInternal = ({
  items,
  itemKey,
  totalItemCount,
  onRenderCell,
  onRenderLoadingCell,
  loadNextPage,
  config,
  classes,
  dimensions,
  setDimensions,
  gridSize,
  version,
  ariaLabel,
}) => {
  // use dimensions to force grid updates by deafult
  const gridVersion = version || dimensions;

  const getItemCountForPage = useCallback((itemIndex, surfaceRect) => {
    let cc = dimensions.colCount;
    // avoid shaking caused by vertical scroll bar
    const surfaceWidthChanges = Math.abs((dimensions.surfaceWidth || 0) - surfaceRect.width);
    if (itemIndex === 0 && surfaceWidthChanges > 20) {
      // respect to min & max column width
      const [colWidth, colCount] = calcColumnWidth(surfaceRect.width, config.maxColumnWidth, config.minColumnWidth);
      setDimensions((cur) => {
        if (colWidth !== cur.colWidth || colCount !== cur.colCount || surfaceRect.width !== cur.surfaceWidth) {
          return { colWidth, colCount, surfaceWidth: surfaceRect.width };
        }
        return cur;
      });
      cc = colCount;
    }
    const rowsPerPage = Math.ceil(config.preferredCellsPerPage / cc);
    return rowsPerPage * cc;
  }, [dimensions, config, setDimensions]);

  const getPageHeight = useCallback(() => {
    const rowsPerPage = Math.ceil(config.preferredCellsPerPage / dimensions.colCount);
    return rowsPerPage * gridSize.rowHeight;
  }, [config.preferredCellsPerPage, dimensions.colCount, gridSize.rowHeight]);

  // insert a clearfix for each page
  const getPageStyle = useCallback(() => ({ overflow: 'auto' }), []);

  const onLoadingNextPage = useCallback(() => {
    if (loadNextPage) {
      loadNextPage();
    }
  }, [loadNextPage]);

  const onRenderCellImpl = useCallback((item, index) => {
    let cell = null;
    if (item === FIRST_LOADING_CELL) {
      if (onRenderLoadingCell) {
        cell = onRenderLoadingCell({ item, index, onLoading: onLoadingNextPage });
      } else {
        cell = (<TilesGridLoadingCell onLoading={onLoadingNextPage} ><AssetLibrarySpinner /></TilesGridLoadingCell>);
      }
    } else if (item === LOADING_CELL) {
      if (onRenderLoadingCell) {
        cell = onRenderLoadingCell({ item, index });
      } else {
        cell = (<TilesGridLoadingCell><AssetLibrarySpinner /></TilesGridLoadingCell>);
      }
    } else {
      cell = onRenderCell({ item, index });
    }
    return (
      <div className="tiles-grid-cell">
        {cell}
      </div>
    );
  }, [onLoadingNextPage, onRenderCell, onRenderLoadingCell]);

  const allItems = useMemo(() => {
    if (items && items.length < totalItemCount) {
      const rowsPerPage = Math.ceil(config.preferredCellsPerPage / dimensions.colCount);
      const preferredCellsPerPage = rowsPerPage * dimensions.colCount;
      const paddingCount = Math.min(totalItemCount - items.length, preferredCellsPerPage);
      if (paddingCount > 1) {
        return [
          ...items,
          FIRST_LOADING_CELL,
          ...Array(paddingCount - 1).fill(LOADING_CELL),
        ];
      }
      return [...items, FIRST_LOADING_CELL];
    }
    return items;
  }, [config, dimensions.colCount, items, totalItemCount]);

  const getItemKey = useCallback(item => item[itemKey], [itemKey]);

  // override this method to set width/height on the cell container
  // almost same as the original implementation here: https://github.com/microsoft/fluentui/blob/master/packages/react/src/components/List/List.tsx#L600
  const onRenderPage = useCallback((pageProps) => {
    const {
      page: { items: gridItems = [], startIndex },
      ...divProps
    } = pageProps;

    const cells = [];

    for (let i = 0; i < gridItems.length; i += 1) {
      const index = startIndex + i;
      const item = gridItems[i];
      const cellKey = getItemKey(item, index) || (item && item.key) || index;
      const cell = onRenderCellImpl(item, index);
      if (cell) {
        const cellWithContainer = (
          <div
            role="listitem"
            className={classnames('ms-List-cell', 'tiles-grid-list-cell')}
            key={cellKey}
            data-list-index={index}
            data-automationid="ListCell"
            style={{ width: gridSize.colWidth, height: gridSize.rowHeight }}
          >
            {cell}
          </div>
        );
        cells.push(cellWithContainer);
      }
    }
    return <div {...divProps}>{cells}</div>;
  }, [getItemKey, gridSize.colWidth, gridSize.rowHeight, onRenderCellImpl]);


  return (
    <div className={classes.tilesGridContainer}>
      <FocusZone>
        <List
          className="tiles-grid"
          items={allItems}
          getItemCountForPage={getItemCountForPage}
          getPageHeight={getPageHeight}
          renderedWindowsAhead={0}
          onRenderCell={onRenderCellImpl}
          onRenderPage={onRenderPage}
          getPageStyle={getPageStyle}
          aria-label={ariaLabel}
          version={gridVersion} // force to rerender when column count/width changed
          getKey={getItemKey}
        />
      </FocusZone>
    </div>
  );
};

TilesGridInternal.propTypes = {
  itemKey: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  totalItemCount: PropTypes.number, // total item count, including not loaded items
  loadNextPage: PropTypes.func,
  onRenderCell: PropTypes.func.isRequired,
  onRenderLoadingCell: PropTypes.func,
  config: PropTypes.shape({
    aspectRatio: PropTypes.number,
    maxColumnWidth: PropTypes.number,
    minColumnWidth: PropTypes.number,
    rowHeight: PropTypes.number,
    preferredCellsPerPage: PropTypes.number,
  }),
  dimensions: PropTypes.shape({
    colCount: PropTypes.number,
    colWidth: PropTypes.number,
    surfaceWidth: PropTypes.number,
  }).isRequired,
  setDimensions: PropTypes.func.isRequired,
  gridSize: PropTypes.shape({
    colWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    rowHeight: PropTypes.number,
  }).isRequired,
  version: PropTypes.shape({}),

  // injected styles
  classes: PropTypes.shape({
    tilesGridContainer: PropTypes.string,
  }).isRequired,
  ariaLabel: PropTypes.string,
};

TilesGridInternal.defaultProps = {
  items: [],
  totalItemCount: 0,
  loadNextPage: null,
  onRenderLoadingCell: null,
  config: DEFAULT_CONFIG,
  version: undefined,
  ariaLabel: '',
};

export const TilesGridStateless = withDefaultStyles(TilesGridInternal, getTilesGridStyle);

export const TilesGrid = ({ items, config, ...rest }) => {
  const [dimensions, setDimensions] = useState({
    colCount: 1,
    colWidth: config.maxColumnWidth,
    surfaceWidth: 0,
  });

  const gridSize = useMemo(() => {
    let finalColWidth;
    let rowHeight;
    if (!_.isEmpty(items) && items.length < dimensions.colCount) {
      // use max width if items are not enough to fill a row
      finalColWidth = config.maxColumnWidth;
      rowHeight = config.aspectRatio ? config.maxColumnWidth * config.aspectRatio : config.rowHeight;
    } else {
      finalColWidth = dimensions.surfaceWidth > 0 ? `${100 * (dimensions.colWidth / dimensions.surfaceWidth)}%` : dimensions.colWidth;
      rowHeight = config.aspectRatio ? dimensions.colWidth * config.aspectRatio : config.rowHeight;
    }
    return { colWidth: finalColWidth, rowHeight };
  }, [config, dimensions, items]);

  return (<TilesGridStateless
    items={items}
    config={config}
    dimensions={dimensions}
    setDimensions={setDimensions}
    gridSize={gridSize}
    {...rest}
  />);
};

TilesGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})),
  totalItemCount: PropTypes.number, // total item count, including not loaded items
  loadNextPage: PropTypes.func,
  onRenderCell: PropTypes.func.isRequired,
  onRenderLoadingCell: PropTypes.func,
  config: PropTypes.shape({
    aspectRatio: PropTypes.number,
    maxColumnWidth: PropTypes.number,
    minColumnWidth: PropTypes.number,
    rowHeight: PropTypes.number,
    preferredCellsPerPage: PropTypes.number,
  }),
  version: PropTypes.shape({}), // used to force grid update
};

TilesGrid.defaultProps = {
  items: [],
  totalItemCount: 0,
  loadNextPage: null,
  onRenderLoadingCell: null,
  config: DEFAULT_CONFIG,
  version: undefined,
};
